import { forwardRef } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChatAssistantMessage } from "@/components/Chat/ChatAssistantMessage";
import { Textarea } from "@/components/ui/textarea";
import { PaperPlaneIcon } from "@radix-ui/react-icons";
import { Button } from "@/components/ui/button";
import { useDocumentChat } from "./DocumentChat.hooks";
import RectTailRight from "@/assets/images/rect_tail_right.svg";
import { AlertMessageDialog } from "@/components/MessageDialog";

type DocumentChatProps = ReturnType<typeof useDocumentChat> & {
  messageLinkType?: "blank" | "self";
};

export const DocumentChat = forwardRef<HTMLDivElement, DocumentChatProps>((props, scrollAnchorRef) => {
  const {
    t,
    isNotIosDevice,
    inputPlaceholder,
    onKeyDown,
    onSubmit,
    recentRallyLimit,
    messages,
    isLoading,
    input,
    setInput,
    apiErrorDialogOpen,
    apiErrorMessage,
    setApiErrorDialogOpen,
    messageLinkType = "blank",
  } = props;

  const documentChatContent = (
    <ScrollArea>
      {/* NOTE: scrollArea 内部のdisplay table指定によって、Child要素の文言が長くなると、幅がChildの要素の幅になるので、以下の指定をWrapperして回避 */}
      <div className="table w-full table-fixed">
        <div className="flex flex-col px-2">
          <ChatAssistantMessage type="first" firstMessage={t("document_chat_first_message")} />

          {messages.map((message, index) => {
            if (message.role === "user") {
              return (
                <div key={index} className="flex items-end justify-end pb-4">
                  <div className="max-w-[calc(100%-80px)] rounded-md bg-[#eee] p-2">
                    <p className="whitespace-pre-line break-words text-sm text-gray-600">{message.content}</p>
                  </div>
                  <img src={RectTailRight} className="mb-2 h-2 w-2" />
                </div>
              );
            } else {
              return (
                <ChatAssistantMessage key={index} type="normal" message={message} messageLinkType={messageLinkType} />
              );
            }
          })}

          {isLoading && messages[messages.length - 1].role === "user" && <ChatAssistantMessage type="loading" />}
        </div>
      </div>
      <div ref={scrollAnchorRef} />
    </ScrollArea>
  );

  const documentChatForm = (
    <div>
      <form
        className="rounded-md border"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <Textarea
          className="h-[112px] min-h-0 resize-none border-0 border-transparent px-3 py-[10px] pt-2 text-sm focus-visible:ring-0 focus-visible:ring-offset-0"
          placeholder={inputPlaceholder}
          onKeyDown={onKeyDown}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          // NOTE: iphoneでチャット画面表示時に、画面下部前面にソフトウェアキーボードが表示されて Textarea が隠れる現象があるため、初期表示ではFocusをしなくして回避する
          autoFocus={isNotIosDevice}
        />
        <div className="flex flex-row justify-end gap-x-4">
          <Button variant="ghost" className="size-10 p-0" disabled={false}>
            <PaperPlaneIcon className="h-4 w-4" />
          </Button>
        </div>
      </form>
      <div className="mr-2 mt-0.5 text-right text-[10px]">{t("chat_send_shortcut")}</div>
    </div>
  );

  return (
    <>
      <div className="flex h-full flex-col bg-white">
        <div className="flex h-full flex-col justify-end gap-y-4">
          {documentChatContent}
          {documentChatForm}
        </div>
        {recentRallyLimit && (
          <div className="absolute rounded bg-white px-1">
            <p className="text-xs text-gray-600">{t("chat_input_memorie_count", { n: recentRallyLimit })}</p>
          </div>
        )}
      </div>
      <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
    </>
  );
});
